<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue'
import type { DataTableColumn } from '@/components/BaseDataTable.vue'
import BaseDataTable from '@/components/BaseDataTable.vue'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import { computed, ref } from 'vue'

const databaseStore = useDatabaseStore()

const statusOptions = computed(() => databaseStore.estimateStatus ?? [])
const filterEstimateStatusOptions = ref<typeof statusOptions.value>([])

function toggleEstiamteStatusFilter(estimateStatus: (typeof statusOptions.value)[number]) {
  if (filterEstimateStatusOptions.value.includes(estimateStatus)) {
    const index = filterEstimateStatusOptions.value.findIndex((e) => e.id === estimateStatus.id)
    if (index === undefined) throw new Error('Unable to get index')
    filterEstimateStatusOptions.value.splice(index, 1)
  } else {
    filterEstimateStatusOptions.value.push(estimateStatus)
  }
}

const rows = computed(() => {
  const filteredEstimates = databaseStore.estimates?.filter(
    (e) =>
      (filterEstimateStatusOptions.value.length > 0
        ? filterEstimateStatusOptions.value.some(
            (estimateStatus) => estimateStatus.id === e.estimate_status_id,
          )
        : true) && e.is_template === false,
  )
  return (
    filteredEstimates?.map((e) => {
      return {
        estimateId: e.id,
        code: e.code,
        name: e.name,
        status: databaseStore.estimateStatus?.find((s) => s.id == e.estimate_status_id)?.name,
      }
    }) ?? []
  )
})

type Row = (typeof rows.value)[number]
const columns = computed<DataTableColumn<Row>[]>(() => {
  return [
    {
      name: 'Code',
      columnDataKey: 'code',
      columnType: 'disabled',
    },
    {
      name: 'Name',
      columnDataKey: 'name',
      columnType: 'disabled',
    },
    {
      name: 'Status',
      columnDataKey: 'status',
      columnType: 'disabled',
    },
  ]
})
</script>

<template>
  <div class="flex h-full w-full flex-col">
    <header class="bg-blue-300 p-4 text-xl font-bold"><h1>Estimates</h1></header>
    <div class="flex h-full w-full flex-col gap-3 p-3">
      <!-- Navigation Tabs -->
      <div class="flex justify-between">
        <div class="flex w-full gap-2 font-bold text-gray-600">
          <div class="inline-flex rounded-md shadow-xs">
            <a
              v-for="status in statusOptions"
              :key="status.id"
              class="cursor-default border border-gray-200 px-4 py-2 text-sm font-medium text-blue-700 first:rounded-s-lg last:rounded-e-lg hover:bg-gray-100 focus:z-10 focus:text-blue-700 focus:ring-2 focus:ring-blue-700"
              :class="filterEstimateStatusOptions.includes(status) ? 'bg-gray-100' : 'bg-white'"
              @click="toggleEstiamteStatusFilter(status)"
            >
              {{ status.name }}
            </a>
          </div>
        </div>
      </div>
      <!-- End Navigation Tabs -->
      <BaseDataTable
        table-name="Estimates"
        :rows="rows"
        :row-key="'estimateId'"
        :columns="columns"
        @row-dbl-click="(row) => router.push(`/estimate/${row.estimateId}`)"
      >
        <template #tableActions>
          <BaseButton @click="router.push({ name: 'EstimateCreate' })">New Estimate</BaseButton>
        </template>
      </BaseDataTable>
    </div>
  </div>
</template>
